import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid } from "@/lib/passwordRules";
import { halamanAwal } from "@/lib/roles";

export default function ForcePasswordChange() {
    const { user, checking, changePassword, logout } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    // Ganti tiap submit (sukses/gagal) -> me-remount ketiga PasswordInput di bawah,
    // memaksa kolomnya balik tersembunyi (lihat PasswordInput.jsx).
    const [pwResetKey, setPwResetKey] = useState(0);
    const navigate = useNavigate();

    if (checking) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.must_change_password) return <Navigate to={halamanAwal(user.role)} replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (!isPasswordValid(next)) return toast.error("Kata sandi baru belum memenuhi semua syarat di bawah");
        if (next !== confirm) return toast.error("Konfirmasi kata sandi tidak cocok");
        setLoading(true);
        const res = await changePassword(current, next);
        setLoading(false);
        setPwResetKey((k) => k + 1);
        if (!res.ok) return toast.error(res.error);
        toast.success("Kata sandi berhasil diperbarui");
        navigate(halamanAwal(user.role));
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="brand-gradient text-white px-8 py-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center border border-white/20">
                            <KeyRound className="w-5 h-5 text-amber-300" />
                        </div>
                        <div>
                            {/* Teal (.brand-gradient) cuma 4.94:1 dgn putih penuh -> teks tidak di-dim */}
                            <div className="text-[11px] uppercase tracking-widest text-white font-semibold">Login Pertama</div>
                            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                                Ubah Kata Sandi Wajib
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="px-8 py-6">
                    <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm mb-5">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div>
                            Sebelum melanjutkan ke dashboard, anda wajib mengganti kata sandi sementara.
                            Kata sandi baru harus memenuhi syarat di bawah dan berbeda dari kata sandi sementara.
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Kata Sandi Sementara</Label>
                            <PasswordInput
                                key={`current-${pwResetKey}`}
                                data-testid="force-password-current-input"
                                required
                                autoComplete="current-password"
                                value={current}
                                onChange={(e) => setCurrent(e.target.value)}
                                className="mt-1.5 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Kata Sandi Baru</Label>
                            <PasswordInput
                                key={`next-${pwResetKey}`}
                                data-testid="force-password-new-input"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={next}
                                onChange={(e) => setNext(e.target.value)}
                                className="mt-1.5 h-11"
                            />
                            <PasswordRequirements password={next} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Konfirmasi Kata Sandi Baru</Label>
                            <PasswordInput
                                key={`confirm-${pwResetKey}`}
                                data-testid="force-password-confirm-input"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="mt-1.5 h-11"
                            />
                        </div>
                        <div className="flex gap-2 pt-1">
                            <Button
                                type="submit"
                                data-testid="force-password-submit-button"
                                disabled={loading}
                                className="flex-1 h-11 bg-[#00B9AD] hover:bg-[#60C0D0]"
                            >
                                {loading ? "Menyimpan..." : "Simpan & Lanjut"}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={async () => { await logout(); navigate("/login"); }}
                                className="h-11"
                            >
                                Batal
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
